import { useEffect, useState } from 'react'
import { useApp } from '@/app/AppContext'
import { Icon } from '@/components/Icon'
import { ApiError } from '@/lib/api'
import {
  fetchNotificationPreferences,
  saveNotificationPreferences,
  type NotificationPreferences,
} from './preferencesApi'
import { BrowserPushPanel } from './BrowserPushPanel'

/**
 * Какие уведомления и куда (тикеты #118, #120).
 *
 * Центр уведомлений не выключается: строка уведомления — запись о том, что от человека
 * ждали, и «я выключил согласования» не ответ на пропущенное согласование. Поэтому колонка
 * «В центре» показана, но не нажимается; выбираются почта и push.
 *
 * Экран называет вслух то, без чего переключатель обманывает: куда уйдут письма (у учётной
 * записи может не быть адреса), подключена ли почта компании и есть ли хоть одно устройство
 * для push — без него галочка «push» ничего не доставит.
 */
export function NotificationSettings() {
  const { t } = useApp()
  const [loaded, setLoaded] = useState<NotificationPreferences | null>(null)
  const [draft, setDraft] = useState<Record<string, boolean>>({})
  const [pushDraft, setPushDraft] = useState<Record<string, boolean>>({})
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)

  const accept = (prefs: NotificationPreferences) => {
    setLoaded(prefs)
    setDraft(Object.fromEntries(prefs.kinds.map((k) => [k.kind, k.email])))
    // Absent means on: older answers and sparse JSON carry no push field at all.
    setPushDraft(Object.fromEntries(prefs.kinds.map((k) => [k.kind, k.push !== false])))
  }

  useEffect(() => {
    const controller = new AbortController()
    fetchNotificationPreferences(controller.signal).then(
      accept,
      (failure: unknown) => {
        if (controller.signal.aborted) return
        setError(failure instanceof ApiError ? failure.message : 'app.error')
      },
    )
    return () => controller.abort()
    // Loaded once per opening of the panel; `t` is not a reason to ask the server again.
  }, [])

  if (!loaded) {
    return (
      <section className="panel stack" style={{ gap: 10, padding: 16 }} aria-label={t('notifications.settings.title')}>
        {error ? (
          <div className="alert alert-danger" role="alert">
            <Icon name="alert" />
            <span>{error === 'app.error' ? t('app.error') : error}</span>
          </div>
        ) : (
          <span className="muted" aria-busy="true">{t('notifications.settings.loading')}</span>
        )}
      </section>
    )
  }

  const pushOf = (kind: string) => loaded.kinds.find((k) => k.kind === kind)?.push !== false
  const emailChanged = loaded.kinds.some((k) => (draft[k.kind] ?? false) !== k.email)
  const pushChanged = loaded.kinds.some((k) => (pushDraft[k.kind] ?? true) !== pushOf(k.kind))
  const changed = emailChanged || pushChanged
  const anyEmail = Object.values(draft).some(Boolean)
  const devices = loaded.pushDevices ?? 0

  const save = () => {
    const email: Record<string, boolean> = {}
    const push: Record<string, boolean> = {}
    for (const kind of loaded.kinds) {
      const next = draft[kind.kind] ?? false
      if (next !== kind.email) email[kind.kind] = next
      const nextPush = pushDraft[kind.kind] ?? true
      if (nextPush !== pushOf(kind.kind)) push[kind.kind] = nextPush
    }
    setSaving(true)
    setSaved(false)
    setError(null)
    const request = Object.keys(push).length > 0
      ? saveNotificationPreferences(email, push)
      : saveNotificationPreferences(email)
    request.then(
      (prefs) => {
        accept(prefs)
        setSaved(true)
        setSaving(false)
      },
      (failure: unknown) => {
        setError(failure instanceof ApiError ? failure.message : t('app.error'))
        setSaving(false)
      },
    )
  }

  return (
    <section className="panel stack" style={{ gap: 12, padding: 16 }} aria-label={t('notifications.settings.title')}>
      <span className="h2">{t('notifications.settings.title')}</span>
      <p className="t-sm secondary" style={{ margin: 0 }}>{t('notifications.settings.lead')}</p>

      {loaded.address == null ? (
        <div className="alert alert-warning" role="note">
          <Icon name="alert" />
          <span>{t('notifications.settings.noAddress')}</span>
        </div>
      ) : (
        <span className="t-sm">{t('notifications.settings.address', { address: loaded.address })}</span>
      )}
      {!loaded.mailConnected && anyEmail && (
        <div className="alert alert-warning" role="note">
          <Icon name="alert" />
          <span>{t('notifications.settings.mailOff')}</span>
        </div>
      )}
      <span className="t-sm" role="note">
        {devices > 0
          ? t('notifications.settings.pushDevices', { count: devices })
          : t('notifications.settings.noPushDevices')}
      </span>

      <div className="tbl-wrap">
        <table className="tbl" aria-label={t('notifications.settings.table')}>
          <thead>
            <tr>
              <th>{t('notifications.settings.kind')}</th>
              <th>{t('notifications.settings.inApp')}</th>
              <th>{t('notifications.settings.email')}</th>
              <th>{t('notifications.settings.push')}</th>
            </tr>
          </thead>
          <tbody>
            {loaded.kinds.map((kind) => {
              const label = t(`notifications.kind.${kind.kind}`)
              return (
                <tr key={kind.kind}>
                  <td>{label}</td>
                  <td>
                    <input type="checkbox" checked disabled
                           aria-label={t('notifications.settings.inAppFor', { kind: label })} />
                  </td>
                  <td>
                    <input type="checkbox" checked={draft[kind.kind] ?? false}
                           disabled={saving || (loaded.address == null && !(draft[kind.kind] ?? false))}
                           aria-label={t('notifications.settings.emailFor', { kind: label })}
                           onChange={(event) => {
                             const value = event.target.checked
                             setSaved(false)
                             setDraft((current) => ({ ...current, [kind.kind]: value }))
                           }} />
                  </td>
                  <td>
                    <input type="checkbox" checked={pushDraft[kind.kind] ?? true}
                           disabled={saving}
                           aria-label={t('notifications.settings.pushFor', { kind: label })}
                           onChange={(event) => {
                             const value = event.target.checked
                             setSaved(false)
                             setPushDraft((current) => ({ ...current, [kind.kind]: value }))
                           }} />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          <Icon name="alert" />
          <span>{error}</span>
        </div>
      )}
      <div className="row" style={{ gap: 8 }}>
        <button className="btn btn-primary btn-sm" type="button" disabled={!changed || saving} onClick={save}>
          {t('notifications.settings.save')}
        </button>
        {saved && !changed && <span className="t-sm muted" role="status">{t('notifications.settings.saved')}</span>}
      </div>
      <p className="t-xs muted" style={{ margin: 0 }}>{t('notifications.settings.unsubscribeNote')}</p>

      <BrowserPushPanel />
    </section>
  )
}
